import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getWords, starWord, unstarWord } from 'students/api/words';
import { IBrowseWord, IWordsFilters, IWordsQuery } from 'students/api/words/types';

interface IWordsState {
  items: IBrowseWord[];
  /**
   * Whether the student's own CEFR level has been folded into the filters yet.
   * Decided once per session: the page is entered from a card that counts one
   * level, so it opens on that level — but re-applying it on every visit would
   * undo a student who has since asked for all levels.
   */
  hasAppliedDefaultLevel: boolean;
  nextPage: number | null;
  totalCount: number;
  filters: IWordsFilters;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
}

export const initialWordsFilters: IWordsFilters = {
  status: 'all',
  wordClass: null,
  level: null,
  q: ''
};

const initialState: IWordsState = {
  items: [],
  hasAppliedDefaultLevel: false,
  nextPage: null,
  totalCount: 0,
  filters: initialWordsFilters,
  isLoading: false,
  isLoadingMore: false,
  error: null
};

export const fetchWords = createAsyncThunk(
  'words/fetchWords',
  async (query: IWordsQuery) => {
    return getWords({ ...query, page: 1 });
  }
);

export const fetchMoreWords = createAsyncThunk(
  'words/fetchMoreWords',
  async (query: IWordsQuery) => {
    return getWords(query);
  }
);

export const toggleStarWord = createAsyncThunk(
  'words/toggleStarWord',
  async ({ id, starred }: { id: number; starred: boolean }) => {
    await (starred ? starWord(id) : unstarWord(id));
    return { id, starred };
  }
);

function setStarred(items: IBrowseWord[], id: number, starred: boolean): void {
  const word = items.find((item) => item.id === id);
  if (!word) return;

  word.studentWord = {
    lastPlayedAt: null,
    lastSolvedAt: null,
    solvedCount: 0,
    playedCount: 0,
    ...word.studentWord,
    starredAt: starred ? new Date().toISOString() : null
  };
}

const wordsSlice = createSlice({
  name: 'words',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<Partial<IWordsFilters>>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    applyDefaultLevel(state, action: PayloadAction<string | null>) {
      state.hasAppliedDefaultLevel = true;
      if (action.payload) state.filters.level = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWords.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.words;
        state.nextPage = action.payload.nextPage;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchWords.rejected, (state) => {
        state.isLoading = false;
        state.error = 'failed';
      })
      .addCase(fetchMoreWords.pending, (state) => {
        state.isLoadingMore = true;
      })
      .addCase(fetchMoreWords.fulfilled, (state, action) => {
        state.isLoadingMore = false;
        state.items = [...state.items, ...action.payload.words];
        state.nextPage = action.payload.nextPage;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchMoreWords.rejected, (state) => {
        state.isLoadingMore = false;
      })
      // Optimistic star toggle: flip on dispatch, flip back if the call fails.
      .addCase(toggleStarWord.pending, (state, action) => {
        setStarred(state.items, action.meta.arg.id, action.meta.arg.starred);
      })
      .addCase(toggleStarWord.rejected, (state, action) => {
        setStarred(state.items, action.meta.arg.id, !action.meta.arg.starred);
      });
  }
});

export const wordsActions = wordsSlice.actions;
export default wordsSlice.reducer;
